import { useAuth0 } from "@auth0/auth0-react";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { Typography } from "@mui/material";
import { Avatar, Box, Button } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Affiliation, GetUserRequest } from "../../proto/api_pb";
import { RegisterUserRequest } from "../../proto/api_pb";
import { TopContainer } from "./styles";
import { styles } from "./styles";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { OmmuseClient } from "../../proto/api_grpc_web_pb";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  AuthContext,
  S3CredentialsContext,
  UserDetailsContext,
} from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import { INITIAL, Profile } from "../ProfileSettings/types";
import { getStorageUsage } from "../../services/storageService";
import { toast } from "react-toastify";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import { useNavigate } from "react-router-dom";
import { ProfileDetailsForm } from "../../components/ProfilePageComponents/ProfileDetailsForm";
import { InitialAffiliation, SocialMediaLinks } from "./types";

import { Affiliation as AffiliationType } from "./types";
import { UserProfileForm } from "../../components/ProfilePageComponents/UserProfileForm";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import { useGetUser } from "../../hooks/useGetUser";
const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

export const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<Profile>(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [usedStorage, setUsedStorage] = useState<number>(0);
  const [skillList, setSkillList] = useState<Array<string>>([]);
  const [genreList, setGenreList] = useState<Array<string>>([]);
  const [profilePicBlob, setProfilePicBlob] = useState<Blob | null>(null);
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const [affiliation, setAffiliation] =
    useState<AffiliationType>(InitialAffiliation);
  const profilePicture = useRef<HTMLInputElement | null>(null);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const { userId } = useContext(AuthContext);
  const { getUserResponse } = useGetUser(userId);
  const [links, setLinks] = useState<SocialMediaLinks>({
    websiteLink: "",
    instagramLink: "",
    spotifyLink: "",
    soundCloudLink: "",
  });
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { user } = useAuth0();
  const { enterprise, isEnterpriseAccount } = useContext(UserDetailsContext);
  const navigate = useNavigate();
  const { getValidAccessToken } = useGetValidAccessToken();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
      const accessToken = await getValidAccessToken();
      const metadata = {
        Authorization: `${accessToken}`,
        "Access-Control-Allow-Origin": "*",
      };
      const request = new GetUserRequest();
      request.setUserId(userId);
      ommuseClient.getUser(request, metadata, async function (err, response) {
        if (err) {
          console.log("get user error");
        } else {
          setProfile((prev) => ({
            ...prev,
            username: response.getUsername(),
            firstName: response.getFirstName(),
            lastName: response.getLastName(),
            connectedWallet: response.getWalletAddressesList()[0],
            bio: response.getBio(),
          }));
          setSkillList([...response.getSkillsList()]);
          setGenreList([...response.getGenresList()]);
          setProfilePic(response.getProfilePicture());
          setLinks({
            websiteLink: response.getWebsiteLink(),
            instagramLink: response.getInstagramLink(),
            spotifyLink: response.getSpotifyLink(),
            soundCloudLink: response.getSoundCloudLink(),
          });
          const affiliationsList = response.getAffiliationsList();
          affiliationsList.forEach((affiliation) => {
            if (affiliation.getName() === "ASCAP") {
              setAffiliation((prev) => ({
                ...prev,
                ASCAP: affiliation.getId(),
              }));
            }
            if (affiliation.getName() === "BMI") {
              setAffiliation((prev) => ({
                ...prev,
                BMI: affiliation.getId(),
              }));
            }
            if (affiliation.getName() === "SESCAP") {
              setAffiliation((prev) => ({
                ...prev,
                SESCAP: affiliation.getId(),
              }));
            }
          });
          const storageUse = await getStorageUsage(
            userId,
            accessToken,
            isEnterpriseAccount ? enterprise?.id : undefined
          );
          typeof storageUse === "number"
            ? setUsedStorage(storageUse)
            : setUsedStorage(0);
        }
        setIsLoading(false);
      });
    };
    fetchUserData();
  }, [enterprise?.id, getValidAccessToken, isEnterpriseAccount, userId]);

  const handleSaveChanges = async () => {
    try {
      setIsSubmitting(true);
      const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
      const request = new RegisterUserRequest();
      let profilePictureString = profile.profilePic;
      request.setUserId(userId);
      request.setFirstName(profile.firstName);
      request.setLastName(profile.lastName);
      request.setUsername(profile.username);
      request.setEmail(user?.email ?? "");
      request.setGenresList(genreList);
      request.addWalletAddresses(profile?.connectedWallet ?? "");
      request.setSkillsList(skillList);
      request.setBio(profile.bio);
      const affiliationList: Affiliation[] = [];
      if (affiliation.ASCAP !== "") {
        const affiliation1 = new Affiliation();
        affiliation1.setName("ASCAP");
        affiliation1.setId(affiliation.ASCAP);
        affiliationList.push(affiliation1);
      }
      if (affiliation.BMI !== "") {
        const affiliation2 = new Affiliation();
        affiliation2.setName("BMI");
        affiliation2.setId(affiliation.BMI);
        affiliationList.push(affiliation2);
      }
      if (affiliation.SESCAP !== "") {
        const affiliation3 = new Affiliation();
        affiliation3.setName("SESCAP");
        affiliation3.setId(affiliation.SESCAP);
        affiliationList.push(affiliation3);
      }
      request.setAffiliationsList(affiliationList);
      request.setEnterpriseDetailsList(
        getUserResponse?.getEnterpriseDetailsList() ?? []
      );
      if (links.websiteLink !== "") request.setWebsiteLink(links.websiteLink);
      if (links.instagramLink !== "")
        request.setInstagramLink(links.instagramLink);
      if (links.spotifyLink !== "") request.setSpotifyLink(links.spotifyLink);
      if (links.soundCloudLink !== "")
        request.setSoundCloudLink(links.soundCloudLink);
      if (profilePicBlob && profilePicBlob.size > 0) {
        const key = await awaitableUploadToS3(
          userId,
          profilePicFile!,
          `profile/${profile.username}`,
          profilePicFile!.type,
          awsAccessKeyId!,
          awsSecretAccessKey!,
          awsSessionToken!,
          () => {}
        );
        profilePictureString = ASSETS_URL + "/" + key;
        request.setProfilePicture(profilePictureString);
      } else {
        request.setProfilePicture(profilePic ?? "");
      }
      const metadata = {
        Authorization: `${await getValidAccessToken()}`,
        "Access-Control-Allow-Origin": "*",
      };
      ommuseClient.registerUser(request, metadata, function (err) {
        if (err) {
          console.log("register user error", err);
          alert(err);
        } else {
          toast.success("Profile updated successfully");
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MobileDrawer />
      <Box sx={{ display: "flex", height: "99vh" }}>
        <Box
          sx={{
            ...styles.mainContainer,
            ...{ paddingBottom: isSmallScreen ? "50px" : 0 },
          }}
        >
          <TopContainer>
            <Box sx={styles.profileBackground}></Box>
            <Box sx={{ paddingLeft: "100px" }}>
              <Box sx={styles.avatarContainer}>
                <Avatar sx={styles.avatar} alt="" src={profilePic ?? ""} />
                <Box sx={styles.userNameContainer}>
                  <Box sx={{ margin: "0px 0px 0px 20px" }}>
                    <Typography sx={styles.userName}>
                      {profile.firstName + " " + profile.lastName}{" "}
                    </Typography>
                    <Typography>
                      Update your photo and personal details.
                    </Typography>
                  </Box>
                  <Box sx={styles.buttonBox}>
                    <Button onClick={handleSaveChanges} sx={styles.button}>
                      {isSubmitting ? (
                        <CircularProgress size={16} sx={{ color: "black" }} />
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button onClick={() => navigate(-1)} sx={styles.skipButton}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </TopContainer>
          {isLoading ? (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <UserProfileForm
                  handleChange={handleChange}
                  profile={profile}
                  usedStorage={usedStorage}
                  skillList={skillList}
                  setSkillList={setSkillList}
                />
                <ProfileDetailsForm
                  genreList={genreList}
                  setGenreList={setGenreList}
                  profilePicture={profilePicture}
                  setProfilePic={setProfilePic}
                  setProfilePicBlob={setProfilePicBlob}
                  setProfilePicFile={setProfilePicFile}
                  profilePic={profilePic}
                  links={links}
                  setLinks={setLinks}
                  isSubmitting={isSubmitting}
                  handleSaveChanges={handleSaveChanges}
                  affiliation={affiliation}
                  setAffiliation={setAffiliation}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
    </>
  );
};
